import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { transcribeOllieOrderingAudio } from 'src/utils/ollieOrderingApi'
import {
  canUseOllieOrderingLiveSpeech,
  canUseOllieOrderingVoice,
  createOllieOrderingLiveSpeech,
  createOllieOrderingRecorder,
  ollieOrderingVoiceFailureMessage,
} from 'src/utils/ollieOrderingVoice'

export const OLLIE_ORDERING_COMPOSER_PLACEHOLDER =
  'I need 2 bacon, 4 tomatoes and a case of potatoes.'

const OllieOrderingComposer = ({
  onSend,
  sending = false,
  disabled = false,
  venueId,
  transcribeAudio = transcribeOllieOrderingAudio,
  createRecorder = createOllieOrderingRecorder,
  createLiveSpeech = createOllieOrderingLiveSpeech,
  liveSpeechSupported = canUseOllieOrderingLiveSpeech(),
  voiceSupported = canUseOllieOrderingLiveSpeech() ||
    canUseOllieOrderingVoice(),
  initialDraft = '',
  autoSendDraft = false,
}) => {
  const [value, setValue] = useState(() => String(initialDraft || ''))
  const autoSentRef = useRef(false)
  const [recording, setRecording] = useState(false)
  const [transcribing, setTranscribing] = useState(false)
  const [voiceError, setVoiceError] = useState(null)
  const [recorder, setRecorder] = useState(null)
  const liveSpeechRef = useRef(null)
  const spokenPrefixRef = useRef('')
  const busy = sending || disabled || transcribing
  const canSend = Boolean(value.trim()) && !busy && !recording
  const showMic = voiceSupported || liveSpeechSupported

  useEffect(() => {
    if (!autoSendDraft || autoSentRef.current) return
    const trimmed = String(initialDraft || '').trim()
    if (!trimmed) return
    autoSentRef.current = true
    onSend?.(trimmed)
  }, [autoSendDraft, initialDraft, onSend])

  useEffect(
    () => () => {
      liveSpeechRef.current?.stop?.()
    },
    [],
  )

  const handleSubmit = async text => {
    const trimmed = String(text || value).trim()
    if (!trimmed || busy || recording) return
    // Clearing on send stops a second tap re-running the same search, which
    // would cost the chef another Ollie use. Same as the app composer.
    setValue('')
    // A refused search — out of Ollie uses, or offline — must not swallow
    // what the chef typed, so the words go back in the box.
    const result = await onSend?.(trimmed)
    if (!result) setValue(trimmed)
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit(value)
    }
  }

  const applySpokenText = spoken => {
    const prefix = spokenPrefixRef.current
    const next = prefix
      ? `${prefix} ${spoken}`.trim()
      : String(spoken || '').trim()
    setValue(next)
    return next
  }

  // A spoken ask leaves the box the way a typed one does: empty, with the
  // prefix forgotten, so the next thing the chef says is not sent with the
  // last ask still bolted on. Refused searches hand the words back.
  const sendSpoken = async spoken => {
    const trimmed = String(spoken || '').trim()
    if (!trimmed) return
    spokenPrefixRef.current = ''
    setValue('')
    const result = await onSend?.(trimmed)
    if (!result) setValue(trimmed)
  }

  const finishLiveSpeech = spoken => {
    liveSpeechRef.current = null
    setRecording(false)
    const prefix = spokenPrefixRef.current
    const next = prefix
      ? `${prefix} ${spoken}`.trim()
      : String(spoken || '').trim()
    if (!next) return
    return sendSpoken(next)
  }

  const handleMic = async () => {
    if (busy) return
    setVoiceError(null)

    if (recording) {
      if (liveSpeechRef.current) {
        liveSpeechRef.current.stop()
        return
      }
      if (recorder) {
        setRecording(false)
        setTranscribing(true)
        try {
          const blob = await recorder.stop()
          setRecorder(null)
          const text = await transcribeAudio({ venueId, blob })
          await sendSpoken(text)
        } catch (error) {
          setVoiceError(ollieOrderingVoiceFailureMessage(error))
        } finally {
          setTranscribing(false)
        }
      }
      return
    }

    if (liveSpeechSupported) {
      try {
        spokenPrefixRef.current = String(value || '').trim()
        const session = createLiveSpeech({
          onTranscript: ({ text }) => {
            applySpokenText(text)
          },
          onEnd: spoken => {
            finishLiveSpeech(spoken)
          },
          onError: error => {
            liveSpeechRef.current = null
            setRecording(false)
            setVoiceError(ollieOrderingVoiceFailureMessage(error))
          },
        })
        liveSpeechRef.current = session
        setRecording(true)
      } catch (error) {
        liveSpeechRef.current = null
        setRecording(false)
        setVoiceError(ollieOrderingVoiceFailureMessage(error))
      }
      return
    }

    try {
      const next = await createRecorder()
      setRecorder(next)
      setRecording(true)
    } catch (error) {
      setRecording(false)
      setRecorder(null)
      setVoiceError(ollieOrderingVoiceFailureMessage(error))
    }
  }

  const micLabel = transcribing
    ? 'Hearing…'
    : recording
    ? liveSpeechSupported
      ? 'Listening…'
      : 'Stop'
    : 'Speak'

  return (
    <Wrapper data-testid='ollie-ordering-composer'>
      <TextArea
        data-testid='ollie-ordering-input'
        value={value}
        onChange={event => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={OLLIE_ORDERING_COMPOSER_PLACEHOLDER}
        disabled={disabled || sending || transcribing}
        rows={3}
        aria-label='Tell Ollie what you need to order'
        aria-live={recording && liveSpeechSupported ? 'polite' : undefined}
      />
      <Actions>
        {showMic ? (
          <MicButton
            type='button'
            onClick={handleMic}
            disabled={disabled || sending || transcribing}
            data-testid='ollie-ordering-mic'
            aria-pressed={recording}
          >
            {micLabel}
          </MicButton>
        ) : null}
        <SendButton
          type='button'
          onClick={() => handleSubmit(value)}
          disabled={!canSend}
          data-testid='ollie-ordering-send'
        >
          {sending ? 'Matching…' : 'Send'}
        </SendButton>
      </Actions>
      {voiceError ? (
        <VoiceError data-testid='ollie-ordering-voice-error'>
          {voiceError}
        </VoiceError>
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 72px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.45;
  font-family: inherit;
  color: ${({ theme }) => theme.colours.bbqBlack};
  background: ${({ theme }) => theme.colours.mayoWhite};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }

  &:disabled {
    background: ${({ theme }) => theme.colours.bgGrey};
    cursor: not-allowed;
  }
`

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`

const SendButton = styled.button`
  background: ${({ theme }) => theme.colours.lettuceGreen};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  min-width: 96px;
  padding: 10px 18px;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colours.avocadoGreen};
  }

  &:disabled {
    background: ${({ theme }) => theme.colours.panSilver};
    color: ${({ theme }) => theme.colours.oliveGrey};
    cursor: not-allowed;
  }
`

const MicButton = styled.button`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 8px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  min-width: 96px;
  padding: 10px 18px;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }

  &:disabled {
    color: ${({ theme }) => theme.colours.oliveGrey};
    cursor: not-allowed;
  }

  &[aria-pressed='true'] {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    background: ${({ theme }) => theme.colours.bgGrey};
  }
`

const VoiceError = styled.p`
  color: ${({ theme }) => theme.colours.chilliRed || '#ff4d4f'};
  font-size: 13px;
  margin: 0;
`

export default OllieOrderingComposer
